import {
  type Store,
  addListener,
  configureStore,
  createListenerMiddleware,
} from '@reduxjs/toolkit';
import {
  type TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';
import appReducer from '../reducers/app/appReducer.ts';
import contactReducer from '../reducers/contact/contactReducer.ts';
import errorReducer from '../reducers/error/errorReducer.ts';
import searchReducer from '../reducers/search/searchReducer.ts';
import servicesReducer from '../reducers/services/servicesReducer.ts';
import storesReducer from '../reducers/stores/storesReducer.ts';
import timeSlotsReducer from '../reducers/timeslots/timeSlotsReducer.ts';
import userReducer from '../reducers/user/userReducer.ts';

const listenerMiddlewareInstance = createListenerMiddleware();

function makeStore(initListenersFunc: () => void) {
  initListenersFunc();
  return configureStore({
    reducer: {
      app: appReducer,
      contact: contactReducer,
      error: errorReducer,
      search: searchReducer,
      services: servicesReducer,
      stores: storesReducer,
      timeSlots: timeSlotsReducer,
      user: userReducer,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(listenerMiddlewareInstance.middleware),
  });
}

export type RootState = ReturnType<ReturnType<typeof makeStore>['getState']>;
export type AppDispatch = ReturnType<typeof makeStore>['dispatch'];
export type StoreType = Store<RootState>;
export const useStateSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useStateDispatch: () => AppDispatch = useDispatch;
export const addAppListener = addListener.withTypes<RootState, AppDispatch>();
export const startAppListening =
  listenerMiddlewareInstance.startListening.withTypes<RootState, AppDispatch>();
export type StartAppListeningFunction = typeof startAppListening;
export default makeStore;
